import React from "react";
import { BudgetCategory, BudgetContingency, BudgetI } from "../types";
import {
  getAboveTheLineEstimate,
  getBelowTheLineEstimate,
  getBudgetCategoryEstimate,
  getBudgetEstimateContingency,
  getBudgetTotalEstimate,
} from "../BudgetItemUtils";
import classNames from "classnames";
import { formatCellMoney } from "./DetailedBudgetGridPrint";
import { getTopSheetCategoryAccountNumber, getTopSheetCategories } from "../topSheetRows.utils";
import { getBudgetPrintBandColor } from "./budgetPrintPalette";

interface TopSheetGridPrintProp {
  data: BudgetCategory[];
  project: string;
  createdAt: string;
  updatedAt: string;
  budgetDetail: Omit<BudgetI, "categories"> | undefined;
  budgetContingency: BudgetContingency | null;
}

export const TopSheetGridPrint: React.FC<TopSheetGridPrintProp> = ({
  data,
  budgetDetail,
  budgetContingency,
}) => {
  const light_bg = "bg-[#f2f2f2]";
  const row_data_style = "whitespace-nowrap border px-4 py-[3px]";
  const currency = budgetDetail?.currency ?? "USD";
  const summaryCategories = getTopSheetCategories(data);

  return (
    <table className="min-w-full border-collapse border text-left text-[16px]">
      <thead>
        <tr className={light_bg}>
          {["Acct #", "Description", "Estimate"].map((boardcol, boardidx) => {
            const same_width_cols = ["Estimate"].includes(boardcol);
            return (
              <th
                scope="col"
                key={boardcol + ""}
                className={`text-black border  px-4 py-1 min-w-[90px] ${
                  boardidx == 0 ? "w-[120px]" : same_width_cols ? "w-[200px]" : ""
                }`}
              >
                <div>{boardcol}</div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className="font-light text-left border relative text-black">
        {summaryCategories.map((rowitem, row_key) => {
          return (
            <tr
              key={row_key + rowitem.budget_category_id}
              // Dark text reads on every palette tint and on bare paper if the tint is dropped.
              className={`${row_data_style} font-semibold text-black`}
              // Same hue order as the on-screen top sheet, in fixed paper colors. Inline, because
              // Tailwind cannot generate class names that are built at runtime. Browsers drop
              // backgrounds when printing unless the element asks to keep its exact colors.
              style={{
                backgroundColor: getBudgetPrintBandColor(row_key),
                printColorAdjust: "exact",
                WebkitPrintColorAdjust: "exact",
              }}
            >
              <td className={row_data_style}>
                <div>{getTopSheetCategoryAccountNumber(rowitem)}</div>
              </td>
              <td className={row_data_style}>
                <div>{rowitem.name}</div>
              </td>
              <td className={row_data_style}>
                <div>
                  {formatCellMoney({
                    value: getBudgetCategoryEstimate(rowitem, data),
                    currency,
                  })}
                </div>
              </td>
            </tr>
          );
        })}
        <tr className={`rounded-b-[16px]`}>
          <td className={classNames(row_data_style, "text-right font-bold")} colSpan={2}>
            <div>TOTAL ABOVE-THE-LINE</div>
          </td>
          <td className={row_data_style}>
            <div>
              {formatCellMoney({
                value: getAboveTheLineEstimate(data),
                emptyIfNoValue: false,
                currency,
              })}
            </div>
          </td>
        </tr>
        <tr className={`rounded-b-[16px]`}>
          <td className={classNames(row_data_style, "text-right font-bold")} colSpan={2}>
            <div>TOTAL BELOW-THE-LINE</div>
          </td>
          <td className={row_data_style}>
            <div>
              {formatCellMoney({
                value: getBelowTheLineEstimate(data),
                emptyIfNoValue: false,
                currency,
              })}
            </div>
          </td>
        </tr>
        <tr>
          <td className={classNames(row_data_style, "text-right font-bold")} colSpan={2}>
            <div>CONTINGENCY</div>
          </td>
          <td className={row_data_style}>
            <div>
              {formatCellMoney({
                value: getBudgetEstimateContingency(data, budgetContingency),
                emptyIfNoValue: false,
                currency,
              })}
              ({budgetDetail?.contingency_percentage ?? 0}%)
            </div>
          </td>
        </tr>
        <tr className="rounded-b-[16px] font-medium bg-gradient-to-r from-primary to-secondary">
          <td className={classNames(row_data_style, "text-right font-bold")} colSpan={2}>
            <div>GRAND TOTAL</div>
          </td>
          <td className={row_data_style}>
            <div>
              {formatCellMoney({
                value: getBudgetTotalEstimate(data, budgetContingency),
                emptyIfNoValue: false,
                currency,
              })}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
